<template>
  <v-container fluid class="pt-0 mt-0">
    <v-row>
      <v-spacer />
      <v-col cols="12" md="11">
        <v-card class="mx-auto" :loading="searchLoading">
          <v-toolbar color="teal" dark class="text-xs-center">
            <v-toolbar-title style="color: white" class="text-wrap">{{
              pageTitle
            }}</v-toolbar-title>
            <v-btn v-if="searchResults" outlined class="ma-1" @click="clearSearch()">{{
              backToBoardsText
            }}</v-btn>

            <v-spacer></v-spacer>

            <v-text-field
              hide-details
              v-model="searchText"
              :label="searchLabel"
              class="ma-0 pa-0"
              v-on:keyup.enter="submitSearch"
            >
            </v-text-field>

            <v-btn @click="submitSearch" icon :loading="searchLoading">
              <v-icon>mdi-magnify</v-icon>
            </v-btn>
          </v-toolbar>
          <div v-if="!searchResults">
            <v-list-item-content class="mx-4 black-text">
              On the European Palm Society discussion forums you can discuss topics
              related to Palms and other exotic plants, swap and make non-commercial plant
              sales, get in touch with members in your area or places that you might be
              visiting or join us at meets and garden visits. You can also upload photos
              to ask for identification, advice, or just share a great picture with
              others.
            </v-list-item-content>

            <v-card class="ma-0 pa-0" v-if="!userIsLoggedIn">
              <v-card-title class="ma-0 pa-0">
                <v-alert type="info" width="100%"
                  >Welcome. You are viewing this forum as a guest. Please consider
                  <router-link class="eps_white" to="/register">registering</router-link> (or if you are a member, 
                  <router-link class="eps_white" to="/login">login</router-link>) to contribute to our community!</v-alert
                >
              </v-card-title>
            </v-card>

            <v-list two-line subheader>
              <v-list-item
                v-for="board in apiData"
                :key="board.title"
                :to="'/forum/' + board.id"
                class="topicrow"
              >
                <v-list-item-content>
                  <h2>
                    <span class="text-wrap mr-2" v-text="htmlDecode(board.title)"> </span>
                    <v-icon color="blue" v-if="board.newBoardMessages" class="d-sm-none">
                      mdi-star
                    </v-icon>
                  </h2>
                  <v-list-item-subtitle class="text-wrap">
                    <h4 class="darkgrey-text">{{ board.description }}</h4>
                  </v-list-item-subtitle>
                  <v-list-item-content class="text-wrap black-text"
                    >Last Post: "{{ board.lastTopicSubject }}"<br /><em>
                      {{ board.lastTopicForename + " " + board.lastTopicSurname }}
                      on
                      {{ formatDateTime(board.lastTopicDate, board.lastTopicTime) }}</em
                    ></v-list-item-content
                  >
                </v-list-item-content>

                <v-list-item-action class="d-none d-sm-flex pr-0 mr-0">
                  <v-chip
                    class="ma-2"
                    :color="getNewMessagesColour(board)"
                    text-color="white"
                  >
                    <v-icon left color="white" v-if="board.newBoardMessages">
                      mdi-star
                    </v-icon>
                    {{ board.topicCount }} topics
                  </v-chip>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>
          <div v-if="searchResults">
            <v-col cols="12" md="11">
              <h2>Search Results</h2>

              <v-pagination
                v-model="pageIndex"
                :length="totalPages"
                :total-visible="15"
                prev-icon="mdi-menu-left"
                next-icon="mdi-menu-right"
                circle
                @input="performSearch()"
              ></v-pagination>

              <v-card v-if="searchResults.length == 0" class="ma-3" outlined>
                <v-list-item-title class="pa-2 ma-2">
                  <h4>
                    No results were found matching '<em>{{ searchText }}</em
                    >'
                  </h4>
                </v-list-item-title>
              </v-card>
              <v-card
                v-for="hit in searchResults"
                :key="hit.postId"
                class="ma-3"
                outlined
              >
                <v-list>
                  <v-list-item>
                    <v-list-item-avatar tile size="80" color="grey">
                      <v-img :src="getFullAvatarUrl(hit.postUserAvatar)"></v-img>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="ma-2" tag="h1">
                        <router-link :to="getPostLink(hit)"
                          >In Topic: {{ hit.subject }} on
                          {{
                            formatDateTime(hit.messageDateSerial, hit.messageTimeSerial)
                          }}
                          {{ hit.postForename }}
                          {{ hit.postSurname }} posted</router-link
                        >
                      </v-list-item-title>
                      <v-card-text
                        v-html="atMax3Paragraphs(formatPostTextForDisplay(hit))"
                      ></v-card-text>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-col>
          </div>

          <div v-if="!forumStats && !searchResults">
            <template>
              <h4 class="ps-3">Loading Forum Statistics</h4>
              <v-sheet
                :color="`grey ${theme.isDark ? 'darken-2' : 'lighten-4'}`"
                class="pa-3"
              >
                <v-skeleton-loader class="mx-auto" type="article"></v-skeleton-loader>
              </v-sheet>
            </template>
          </div>

          <div v-if="forumStats && activeUserData && activeUserData.activeUserCount > 0">
            <v-col cols="12" class="black-text">
              {{ getActiveUserDescription }}. In the last 24 hours there {{ getGuestVisitorsDescription }}
            </v-col>
          </div>

          <div v-if="forumStats">
            <v-col cols="12">
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left black-text">Forum Stats</th>
                      <th class="text-left black-text">Users Reading Posts</th>
                      <th class="text-left black-text">Active Countries</th>
                      <!-- <th class="text-left">
                        Topics Read
                      </th> -->
                      <th class="text-left black-text">Members Posting</th>
                      <th class="text-left black-text">Active Topics</th>
                      <th class="text-left black-text">New Posts</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Today</td>
                      <td>{{ forumStats.memberUserReadsDay }}</td>
                      <td>{{ forumStats.memberCountryReadsDay }}</td>
                      <!-- <td>{{ forumStats.memberTopicReadsDay }}</td> -->
                      <td>{{ forumStats.memberPostsDay }}</td>
                      <td>{{ forumStats.memberTopicsDay }}</td>
                      <td>{{ forumStats.postCountDay }}</td>
                    </tr>
                    <tr>
                      <td>This Month</td>
                      <td>{{ forumStats.memberUserReadsMonth }}</td>
                      <td>{{ forumStats.memberCountryReadsMonth }}</td>
                      <!-- <td>{{ forumStats.memberTopicReadsMonth }}</td> -->
                      <td>{{ forumStats.memberPostsMonth }}</td>
                      <td>{{ forumStats.memberTopicsMonth }}</td>
                      <td>{{ forumStats.postCountMonth }}</td>
                    </tr>
                    <tr>
                      <td>Last 12 Months</td>
                      <td>{{ forumStats.memberUserReadsTotal }}</td>
                      <td>{{ forumStats.memberCountryReadsTotal }}</td>
                      <!-- <td>{{ forumStats.memberTopicReadsTotal }}</td> -->
                      <td>{{ forumStats.memberPostsTotal }}</td>
                      <td>{{ forumStats.memberTopicsTotal }}</td>
                      <td>{{ forumStats.postCountTotal }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
          </div>
        </v-card>
      </v-col>
      <v-spacer />
    </v-row>
  </v-container>
</template>

<script>
import userFunctions from "@/functions/userFunctions";
import dateFunctions from "@/functions/dateFunctions";
import serverFunctions from "@/functions/serverFunctions";
import stringFunctions from "@/functions/stringFunctions";
import forumFunctions from "@/functions/forumFunctions";
import dataStore from "../functions/dataStore";

export default {
  inject: {
    theme: {
      default: { isDark: false },
    },
  },
  props: ["forumList"],
  computed: {
    getGuestVisitorsDescription() {
      let guestVisitors = this.activeUserData
        ? this.activeUserData.anonymousUsersLast24Hours
        : 0;
      return guestVisitors == 1
        ? "has been 1 guest"
        : "have been " + guestVisitors + " guests";
    },
    getActiveUserDescription() {
      let membersOnline = this.activeUserData.result;

      switch (this.activeUserData.activeUserCount) {
        case 0:
          return "no members have been online in the past 30 minutes";
        case 1:
          return membersOnline.length > 0
            ? membersOnline[0] + " is the only member online in the past 30 minutes"
            : "no members have been online in the last 30 minutes";
        default:
          var activeUserMessage =
            "There have been " +
            this.activeUserData.activeUserCount +
            " members online in the last 30 minutes including ";
          for (let i = 0; i < membersOnline.length; i++) {
            activeUserMessage += membersOnline[i];

            let distanceFromEnd = membersOnline.length - 1 - i;
            switch (distanceFromEnd) {
              case 0:
                break;
              case 1:
                activeUserMessage += " and ";
                break;
              default:
                activeUserMessage += ", ";
            }
          }

          return activeUserMessage;
      }
    },
    pageTitle() {
      if (this.searchResults) {
        return "";
      }
      return this.$vuetify.breakpoint.smAndDown
        ? "Forums"
        : "The European Palm Society Forums";
    },
    searchLabel() {
      return this.$vuetify.breakpoint.smAndDown ? "Search..." : "Enter search text...";
    },
    backToBoardsText() {
      return this.$vuetify.breakpoint.smAndDown ? "Back" : "Back To Boards";
    },
    userIsLoggedIn() {
      return userFunctions.userIsLoggedIn();
    },
  },
  methods: {
    getNewMessagesColour(board) {
      return board.newBoardMessages ? "primary" : "green";
    },
    formatDateTime(date, time) {
      return dateFunctions.formatPalmDateTime(date, time);
    },
    getPostLink(post) {
      return "/post/" + post.boardId + "/" + post.topicId + "/" + post.postId;
    },
    getUser(id) {
      return userFunctions.getUser(id);
    },
    formatPostTextForDisplay(post) {
      return forumFunctions.formatPostTextForDisplay(post);
    },
    atMax3Paragraphs(text) {
      return stringFunctions.atMax3Paragraphs(text);
    },
    getFullAvatarUrl(avatar) {
      return userFunctions.getFullAvatarUrl(avatar);
    },
    htmlDecode(text) {
      return stringFunctions.htmlDecode(text);
    },
    async statsSuccess(result) {
      this.forumStats = await result.json();
    },
    statsError(data) {
      console.log(data);
    },
    async activeUsersSuccess(result) {
      this.activeUserData = await result.json();
    },
    activeUsersError(data) {
      console.log(data);
    },
    async getForumStats() {
      const forumStatsRequest = serverFunctions.getDataRequestObject();
      serverFunctions.getPostResponseAsync(
        "/getForumActivity",
        forumStatsRequest,
        this.statsSuccess,
        this.statsError
      );
    },
    async getActiveUsers() {
      serverFunctions.getResponseAsync(
        "/getForumActiveUsers",
        this.activeUsersSuccess,
        this.activeUsersError
      );
    },
    async getAPIData() {
      const boardRequest = serverFunctions.getDataRequestObject(
        userFunctions.getLoggedInUser()
      );
      this.apiData = await serverFunctions.getPostResponse("/getBoards", boardRequest);
      userFunctions.updateLastViewed("forums");
    },
    clearSearch() {
      this.searchResults = null;
      this.searchText = null;
      this.$router.push({ name: "ForumBoards" });
      this.pageIndex = 1;
      this.getAPIData();
    },
    submitSearch() {
      this.$router.push({
        name: "ForumBoardsSearch",
        params: { search: this.searchText },
      });
      this.pageIndex = 1;
      this.performSearch();
    },
    async performSearch() {
      dataStore.setPagingValue("boards", this.pageIndex);
      this.searchLoading = true;
      const searchRequest = serverFunctions.getSearchForumRequestObject(
        userFunctions.getLoggedInUser(),
        this.searchText,
        this.pageSize,
        this.pageIndex
      );

      let searchResponse = (this.searchResults = await serverFunctions.getRawPostResponse(
        "/searchAllBoards",
        searchRequest
      ));
      if (searchResponse.resultsCount) {
        this.totalPages = Math.floor(searchResponse.resultsCount / this.pageSize) + 1;
      } else {
        this.totalPages = 0;
      }
      this.searchResults = searchResponse.result;
      this.searchLoading = false;
    },
  },
  data: function () {
    return {
      searchResults: null,
      apiData: null,
      searchText: null,
      searchLoading: false,
      pageIndex: parseInt(dataStore.getPagingValue("boards")),
      pageSize: 25,
      totalPages: 10,
      forumStats: null,
      activeUserData: null,
      attrs: {
        class: "mb-6",
        boilerplate: false,
        elevation: 2,
      },
    };
  },
  beforeMount() {
    if (this.$route.params.search) {
      this.searchText = this.$route.params.search;
      this.performSearch();
      return;
    }
    this.getAPIData();
    this.getForumStats();
    this.getActiveUsers();
  },
};
</script>
